import { useCallback, useEffect, useState } from "react";
import { AppIcon } from "../AppIcon";
import type { Order, Ride, Rides } from "../../api/types";
import { useLive } from "../../app/useLive";
import { kmText, payPill } from "../../features/couriers/board";
import { addressOf } from "../../features/orders/order-view";
import { t } from "../../i18n";
import { formatTime } from "../../lib/time";
import { AppPage } from "../AppScreens";
import type { AppEnv } from "../AppShell";
import { appDevice } from "../app-storage";
import { CashView } from "./CashView";
import { Dialog } from "../../ui/Dialog";
import { DeliverPane } from "./DeliverPane";
import { ProblemPane } from "./ProblemPane";
import { ReleasePane } from "./ReleasePane";
import { inNativeApp } from "../native";
import { useSharePosition } from "./share-position";

type Tab = "ready" | "rides" | "cash";
interface Pane {
  kind: "deliver" | "problem" | "release";
  orderId: string;
}

/**
 * Only digits for "📞 Bel" (prototype tel:digits). Takeaway.com and Uber Eats hide the customer's number behind
 * their own exchange and a code: the phone dials the number, waits (",") and then gives the code, in one tap.
 */
const phoneLink = (phone: string | undefined, code?: string) =>
  `tel:${(phone ?? "").replace(/[^\d+]/g, "")}${code ? `,${code.replace(/\D/g, "")}` : ""}`;

/** The pill of a ride: "Te innen € 19,20" or "Betaald · Bancontact" (prototype payBox). */
function Pill({ order }: { order: Order }) {
  const pill = payPill(order);
  return <span className={`pill ${pill.tone}`}>{pill.text}</span>;
}

/**
 * Apple Maps along all the rides on the way, in Tafo's order: the stops as waypoints, the last one as destination
 * (Apple's unified Maps links, iOS 18.4 and later; Furkan 18/10/2026: "Voeg stop toe" exists in Apple Maps).
 */
function appleRouteUrl(rides: readonly Order[]): string {
  const stops = rides.map((ride) => `&waypoint=${encodeURIComponent(mapsText(ride))}`);
  const destination = encodeURIComponent(rides.length === 0 ? "" : mapsText(rides.at(-1)!));
  return `https://maps.apple.com/directions?destination=${destination}${stops.slice(0, -1).join("")}&mode=driving`;
}

/** "Kerkstraat 3, 9140 Temse, België": what a map finds. */
function mapsText(ride: Order): string {
  const street = [ride.customer.street, ride.customer.number].filter(Boolean).join(" ");
  const place = [ride.customer.postcode, ride.customer.zone].filter(Boolean).join(" ");
  return [street, place, "België"].filter(Boolean).join(", ");
}

/** A note of the customer on the order (not only spaces). */
const hasNote = (ride: Order) => ride.note !== null && ride.note.trim() !== "";

/** The address, and what is to be collected right of it on the same line (Furkan, 18/10/2026: lower cards). */
function AddressRow({ ride }: { ride: Order }) {
  return (
    <div className="ph-addr">
      <b>{addressOf(ride)}</b>
      <Pill order={ride} />
    </div>
  );
}

/**
 * The courier's app (prototype renderPhone for the courier): Klaar (when couriers take their rides
 * themselves), Ritten (ready to leave, then on the way in the order of the route: Bel, Navigeer, ⚠,
 * Geleverd; "Terugzetten" for a ride taken by mistake) and Cash (delivered, not settled yet). Kept current
 * by the live channel.
 */
export function CourierApp({ env }: { env: AppEnv }) {
  const { api, session, toast, report } = env;
  const text = t.tafoApp.courier;
  const me = session.staff.name;
  const [rides, setRides] = useState<Rides | null>(null);
  const [tab, setTab] = useState<Tab | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [pane, setPane] = useState<Pane | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.request<Rides>("GET", "/device/rides", { retry: true }).then(setRides).catch(report);
  }, [api, report]);
  useEffect(load, [load]);

  useLive(
    appDevice.read() ?? "",
    {
      onMessage: (message) => {
        if (
          message.type === "order.created" ||
          message.type === "order.updated" ||
          message.type === "order.deleted" ||
          message.type === "couriers.updated" ||
          message.type === "general.updated" ||
          message.type === "staff.updated" ||
          message.type === "resync"
        ) {
          load();
        }
      },
      onRefused: env.onLiveRefused,
    },
    env.session.token,
  );

  // his position, only during a ride (voorwaarden 7.4): on the way, and back to the shop where it ends
  const sharing = useSharePosition(
    api,
    (rides?.mine.some((ride) => ride.departedAt !== null) ?? false) || rides?.returning === true,
  );

  if (rides === null) return <AppPage head={env.head}>{null}</AppPage>;

  const self = rides.mode === "self";
  const current: Tab = tab === null || (tab === "ready" && !self) ? (self ? "ready" : "rides") : tab;
  const waiting = rides.mine.filter((ride) => ride.departedAt === null);
  const onTheWay = rides.mine.filter((ride) => ride.departedAt !== null);
  const all = [...rides.free, ...rides.mine];
  const open = pane === null ? null : (all.find((ride) => ride.id === pane.orderId) ?? null);
  /** "Geleverd" and "Terugzetten" open a window over the list, not a page of their own (Furkan, 18/10/2026). */
  const delivering = pane?.kind === "deliver" ? open : null;
  const releasing = pane?.kind === "release" ? open : null;
  const asking = pane?.kind === "problem" ? open : null;

  const run = async (work: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try {
      await work();
    } catch (error) {
      report(error);
      load();
    } finally {
      setBusy(false);
    }
  };

  /** "Neem n ritten" (prototype phTake): one that another courier took meanwhile is refused. */
  const take = (ids: string[]) =>
    run(async () => {
      const answer = await api.request<{ taken: number }>("POST", "/device/rides/take", { body: { orderIds: ids } });
      toast(text.taken(me, answer.taken));
      setSelected(new Set());
      setTab("rides");
      load();
    });

  /** "🛵 Vertrek": the fastest route along all of them, the mail "onderweg" to the customers. */
  const depart = () =>
    run(async () => {
      const departure = await api.request<{ rides: unknown[]; totalKm: number }>("POST", "/device/rides/depart");
      toast(
        text.departed(
          me,
          departure.rides.length,
          departure.totalKm.toLocaleString("nl-BE", { maximumFractionDigits: 1 }),
        ),
      );
      load();
    });

  /** "Ik ben terug in de zaak": the ride ends, also for a phone that does not see the shop. */
  const backAtShop = () =>
    run(async () => {
      await api.request<{ sharing: boolean }>("POST", "/device/rides/back");
      load();
    });

  // everything delivered: on his way back, his position is shared until the shop (Furkan, 18/10/2026)
  const wayBack = rides.returning === true && onTheWay.length === 0 && (
    <div className="ph-back">
      <p className={`proto ph-tight ph-gps ${sharing === "off" ? "on" : sharing}`} role="status">
        <AppIcon name="home" size={16} />
        <span>
          <b>{text.returning}</b> ·{" "}
          {sharing === "denied" || sharing === "unavailable" ? text.returningOff : text.returningNote}
        </span>
      </p>
      <button type="button" className="btn ph-big" disabled={busy} onClick={() => void backAtShop()}>
        <AppIcon name="check" />
        {text.backAtShop}
      </button>
    </div>
  );

  const tabs = (
    <nav className="ph-tabs" aria-label={t.tafoApp.title}>
      {(
        [
          ...(self ? ([["ready", t.tafoApp.tabs.ready, rides.free.length]] as const) : []),
          ["rides", t.tafoApp.tabs.rides, rides.mine.length],
          ["cash", t.tafoApp.tabs.cash, rides.cash?.orderIds.length ?? 0],
        ] as const
      ).map(([key, title, count]) => (
        <button
          key={key}
          type="button"
          aria-pressed={current === key}
          onClick={() => {
            setTab(key);
          }}
        >
          {title}
          {count > 0 && <span className="badge">{count}</span>}
        </button>
      ))}
    </nav>
  );

  /** "Terugzetten": small and apart from the other actions, and asked once more (ReleasePane). */
  /** "Terugzetten": only its red symbol, at the right end of the row (Furkan, 18/10/2026). */
  const releaseButton = (ride: Ride) => (
    <button
      type="button"
      className="btn ph-iconbtn ph-undo"
      aria-label={text.releaseLabel(ride.code)}
      title={text.release}
      onClick={() => {
        setPane({ kind: "release", orderId: ride.id });
      }}
    >
      <AppIcon name="undo" />
    </button>
  );

  const card = (ride: Ride, extra: string) => (
    <div className="ph-card" key={ride.id}>
      <div>
        <AddressRow ride={ride} />
        <div className="ph-coderow">
          <span className="proto">{[ride.code, kmText(ride.km)].filter(Boolean).join(" · ") + extra}</span>
          {releaseButton(ride)}
        </div>
      </div>
    </div>
  );

  let body;
  if (current === "ready") {
    body =
      rides.free.length === 0 ? (
        <p className="proto ph-empty">{text.noneReady}</p>
      ) : (
        <>
          {rides.free.map((ride) => {
            const on = selected.has(ride.id);
            return (
              <label className={`ph-card sel${on ? " on" : ""}`} key={ride.id}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => {
                    const next = new Set(selected);
                    if (on) next.delete(ride.id);
                    else next.add(ride.id);
                    setSelected(next);
                  }}
                />
                <div>
                  <AddressRow ride={ride} />
                  <span className="proto">
                    {[text.readySince(ride.code, formatTime(ride.createdAt)), kmText(ride.km)]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
              </label>
            );
          })}
          {/* aanvinken en dan één rode knop, altijd onderaan in beeld; geen "Neem alle ritten" meer (Donass,
              18/10/2026: met 19 ritten klaar nam niemand ze allemaal, en de knop stond onder een lange lijst) */}
          <div className="ob-row ob-addbar">
            <button
              type="button"
              className="btn primary ph-big"
              disabled={busy || selected.size === 0}
              onClick={() => void take([...selected].filter((id) => rides.free.some((ride) => ride.id === id)))}
            >
              {text.take(selected.size)}
            </button>
          </div>
        </>
      );
  } else if (current === "rides") {
    body =
      rides.mine.length === 0 ? (
        <p className="proto ph-empty">{self ? text.noRidesSelf : text.noRidesTill}</p>
      ) : (
        <>
          {waiting.length > 0 && (
            <>
              <div className="ph-sec">{text.readyToLeave}</div>
              {waiting.map((ride) => card(ride, ""))}
              <button type="button" className="btn primary ph-big" disabled={busy} onClick={() => void depart()}>
                <AppIcon name="courier" />
                {text.depart(waiting.length)}
              </button>
              {waiting.length > 1 && <p className="proto ph-tight">{text.departNote(waiting.length)}</p>}
            </>
          )}
          {onTheWay.length > 0 && (
            <>
              <div className="ph-sec">{text.onTheWay}</div>
              {sharing !== "off" && (
                <p className={`proto ph-tight ph-gps ${sharing}`} role="status">
                  <AppIcon name="pin" size={16} />
                  {sharing === "on" && inNativeApp() ? text.sharingNative : text.sharing[sharing]}
                </p>
              )}
              {/* the whole route in the map the courier uses, at once (Furkan, 19/10/2026: "bizim için önemli olan
                  aynı anda navige etmek"); Google and Apple take every stop (Waze takes one: left out, 19/10/2026). One ride: the
                  same three buttons to its address; none on the cards */}
              <div className="ph-routehead">
                <AppIcon name="route" size={18} />
                {text.fullRoute(onTheWay.length)}
              </div>
              <div className="ph-routes">
                <a
                  className="btn primary"
                  href={rides.routeUrl ?? onTheWay[0]!.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {text.google}
                </a>
                <a className="btn primary" href={appleRouteUrl(onTheWay)} target="_blank" rel="noopener noreferrer">
                  {text.apple}
                </a>
              </div>
              {onTheWay.length > 1 && <p className="proto ph-tight">{text.routeNote}</p>}
              {onTheWay.map((ride, index) => (
                <div className="ph-card road" key={ride.id}>
                  <div className="ph-n">{index + 1}</div>
                  <div>
                    <AddressRow ride={ride} />
                    <div className="ph-coderow">
                      <span>
                        {text.arrival(ride.customer.name ?? "", ride.eta === null ? "" : formatTime(ride.eta))}
                      </span>
                    </div>
                    {ride.problems.length > 0 && (
                      <span className="ph-note bad">
                        <AppIcon name="problem" size={16} />
                        {text.problemOnBoard(ride.problems.at(-1)!.reason, formatTime(ride.problems.at(-1)!.at))}
                      </span>
                    )}
                    <div className="ph-acts">
                      <a
                        className="btn ph-iconbtn"
                        href={phoneLink(ride.customer.phone, ride.customer.phoneCode)}
                        aria-label={text.call}
                      >
                        <AppIcon name="phone" />
                      </a>
                      {/* a note of the customer: the triangle burns yellow, a tap shows the note above the
                          problems (Furkan, 18/10/2026: draws more attention than a line on the card) */}
                      <button
                        type="button"
                        className={hasNote(ride) ? "btn ph-iconbtn ph-hasnote" : "btn ph-iconbtn"}
                        aria-label={hasNote(ride) ? text.noteLabel(ride.code) : text.problemLabel(ride.code)}
                        onClick={() => {
                          setPane({ kind: "problem", orderId: ride.id });
                        }}
                      >
                        <AppIcon name="problem" />
                      </button>
                      <button
                        type="button"
                        className="btn primary ph-delivered"
                        onClick={() => {
                          setPane({ kind: "deliver", orderId: ride.id });
                        }}
                      >
                        {text.delivered}
                      </button>
                      {releaseButton(ride)}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      );
  } else {
    body = <CashView statement={rides.cash} />;
  }

  return (
    <AppPage head={env.head} tabs={tabs} scrollKey={current}>
      {wayBack}
      {body}
      {delivering !== null && (
        <Dialog
          title={addressOf(delivering)}
          size="narrow"
          onClose={() => {
            setPane(null);
          }}
        >
          <DeliverPane
            api={api}
            ride={delivering}
            bankQr={rides.bankQr}
            inDialog
            onBack={() => {
              setPane(null);
            }}
            onDelivered={(how) => {
              toast(text.deliveredToast(delivering.code, how));
              setPane(null);
              load();
            }}
            report={report}
          />
        </Dialog>
      )}
      {asking !== null && (
        <Dialog
          title={addressOf(asking)}
          size="narrow"
          onClose={() => {
            setPane(null);
          }}
        >
          <ProblemPane api={api} ride={asking} report={report} />
        </Dialog>
      )}
      {releasing !== null && (
        <Dialog
          title={text.releaseQuestion(releasing.code)}
          size="narrow"
          onClose={() => {
            setPane(null);
          }}
        >
          <ReleasePane
            api={api}
            ride={releasing}
            self={self}
            inDialog
            onBack={() => {
              setPane(null);
            }}
            onReleased={() => {
              toast(text.released(releasing.code));
              setPane(null);
              load();
            }}
            report={report}
          />
        </Dialog>
      )}
    </AppPage>
  );
}
